import type { ReactNode } from 'react';

import classNames from 'classnames';

import type { Timecode, TimecodeOptions } from '@iconik-io/core/src/timecode';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import styles from './timecode.module.css';

type SeparatorProps = {
  dropFrame?: boolean;
};

const Separator = ({ dropFrame }: SeparatorProps) => (
  <span className={styles.separator}>{dropFrame ? ';' : ':'}</span>
);

type Callbacks = {
  onChangeHours: (v: string) => void;
  onChangeMinutes: (v: string) => void;
  onChangeSeconds: (v: string) => void;
  onChangeFrames: (v: string) => void;
  onBlur: () => void;
};

export type TimecodeRenderer = (
  props: TimecodeProps,
  state: TimecodeState,
  callbacks?: Callbacks,
) => ReactNode;

export type TimecodeProps = {
  value?: number;
  timecodeOptions: TimecodeOptions;
  changeValue?: (value: number) => void;
  renderer?: TimecodeRenderer;
  aria?: AriaProps;
};

export type TimecodeState = {
  time: Timecode;
  value: number;
  hours?: string;
  minutes?: string;
  seconds?: string;
  frames?: string;
  lastSeparator?: string;
};

export const fieldRenderer: TimecodeRenderer = (
  props: TimecodeProps,
  state: TimecodeState,
  callbacks?: Callbacks,
) => {
  const { hours, minutes, seconds, frames } = state;
  const parts = state.time.toParts();
  const longFrames = props.timecodeOptions.frameRate
    ? props.timecodeOptions.frameRate > 100
    : false;

  return (
    <div
      className={styles.timecode_wrapper}
      aria-label="Timecode input"
      {...ariaToReactProps(props.aria)}
    >
      <input
        placeholder={parts.hours || '00'}
        value={hours || ''}
        className={styles.timecode}
        onChange={(e) => callbacks?.onChangeHours(e.target.value)}
        onBlur={callbacks?.onBlur}
        aria-label="Hours"
      />
      <Separator />
      <input
        placeholder={parts.minutes || '00'}
        value={minutes || ''}
        className={styles.timecode}
        onChange={(e) => callbacks?.onChangeMinutes(e.target.value)}
        onBlur={callbacks?.onBlur}
        aria-label="Minutes"
      />
      <Separator />
      <input
        placeholder={parts.seconds || '00'}
        value={seconds || ''}
        className={styles.timecode}
        onChange={(e) => callbacks?.onChangeSeconds(e.target.value)}
        onBlur={callbacks?.onBlur}
        aria-label="Seconds"
      />
      <Separator dropFrame={props.timecodeOptions.isDropFrame} />
      <input
        placeholder={parts.frames || (longFrames ? '000' : '00')}
        value={frames || ''}
        className={classNames(
          styles.timecode,
          longFrames ? styles.timecode_large : '',
        )}
        onChange={(e) => callbacks?.onChangeFrames(e.target.value)}
        onBlur={callbacks?.onBlur}
        aria-label="Frames"
      />
    </div>
  );
};

export const stringRenderer: TimecodeRenderer = (_props, state): ReactNode =>
  state.time.toSMPTE();
